// Plan 08-02 — POST /api/auth/exchange typed call.
//
// Bootstrap path: NO Authorization header (this IS the auth handshake).
// On success, persists the bearer atom (B12 firewall — only user_uuid + jwt
// + exp_ms + idp_kind, never game state).
//
// Error mapping (per Phase 2 oracle hardening):
//   - 401 → AuthInvalidError (also clears the bearer atom; oracle: every 401
//     sub-cause emits a byte-equal ErrorEnvelope, so we must NOT leak cause).
//   - 423 → AuthLockedError carrying details.pending_deletion_until (lawful
//     disclosure to the authenticated principal who typed DELETE/RADERA).
//   - 5xx → AuthTransientError(status); caller (silent-refresh, AuthProvider
//     login) decides whether to retry. apiFetch retries 5xx for non-auth
//     endpoints; the bootstrap exchange does NOT retry to keep semantics
//     simple (a stuck IdP is a user-visible failure, not a silent loop).
//   - Network failure (fetch rejects) → AuthTransientError(0).
//
// V1 versioning header (DEC-018): Accept + Content-Type both pinned to
// application/vnd.runegambit.v1+json on every request.

import { clearAuth, saveAuth } from '../stores/auth.js'
import {
  type AuthExchangeResult,
  AuthInvalidError,
  AuthLockedError,
  AuthTransientError,
  type IdpKind,
} from './types.js'

const V1 = 'application/vnd.runegambit.v1+json'

export interface ExchangeIdpInput {
  idp_kind: IdpKind
  id_token: string
  nonce: string
}

// OAuth-code-flow inputs for web Google initial sign-in only. The backend
// swaps the code for an id_token at Google's token endpoint using the
// configured `GOOGLE_CLIENT_SECRET`, then runs the same downstream user-upsert
// + session-mint pipeline as the id_token branch. Replay protection comes
// from (a) Google enforcing single-use on the code, (b) postMessage
// origin-binding from `accounts.google.com` → the parent window of the popup,
// and (c) the server-side client_secret. No PKCE — `initCodeClient` doesn't
// expose it, and it would be defense-in-depth that the Google-documented
// flow does not require.
export interface ExchangeGoogleCodeInput {
  code: string
  redirect_uri: string
}

interface SessionTokenResponse {
  user_uuid: string
  session_jwt: string
  expires_at: number
}

interface LockedResponse {
  error?: {
    code?: string
    message?: string
    details?: {
      pending_deletion_until?: number
    }
  }
}

function getApiBaseUrl(): string {
  const url = import.meta.env?.VITE_API_BASE_URL
  if (!url || url.startsWith('<')) {
    // Placeholder still in place — let the caller see a clean fetch error
    // rather than a silent localhost rewrite. The runtime warning emitted
    // from main.tsx (CSP placeholder) covers the operator path.
    throw new AuthTransientError(0, 'VITE_API_BASE_URL not configured')
  }
  return url
}

export async function exchangeIdpToken(
  input: ExchangeIdpInput,
): Promise<AuthExchangeResult> {
  const url = `${getApiBaseUrl()}/api/auth/exchange`

  let response: Response
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: {
        Accept: V1,
        'Content-Type': V1,
      },
      body: JSON.stringify({
        idp_kind: input.idp_kind,
        id_token: input.id_token,
        nonce: input.nonce,
      }),
    })
  } catch {
    throw new AuthTransientError(0, 'Network error during /api/auth/exchange')
  }

  if (response.ok) {
    const body = (await response.json()) as SessionTokenResponse
    const result: AuthExchangeResult = {
      user_uuid: body.user_uuid,
      session_jwt: body.session_jwt,
      exp_ms: body.expires_at,
      idp_kind: input.idp_kind,
    }
    saveAuth(result)
    return result
  }

  if (response.status === 401) {
    // Drop the body unread — Phase 2 oracle hardening returns byte-equal
    // ErrorEnvelopes across all 401 sub-causes, so there is nothing to
    // surface beyond "auth invalid".
    clearAuth()
    throw new AuthInvalidError()
  }

  if (response.status === 423) {
    const body = (await response.json().catch(() => ({}))) as LockedResponse
    const pending = body.error?.details?.pending_deletion_until ?? 0
    throw new AuthLockedError(pending)
  }

  if (response.status >= 500) {
    throw new AuthTransientError(response.status)
  }

  // Other 4xx (e.g. 400, 406): surface as a generic transient with the status
  // so the caller can render a user-visible "please try again" UX. We do not
  // ship a typed error here because the only callers (silent-refresh,
  // AuthProvider.loginWithApple/Google) treat all non-200/401/423/5xx as
  // "the IdP handshake failed; route to splash login".
  throw new AuthTransientError(
    response.status,
    `Unexpected status ${response.status}`,
  )
}

/**
 * Web Google initial-sign-in path: POST the OAuth auth code + PKCE verifier;
 * the backend swaps it for an id_token at Google's token endpoint and runs
 * the same downstream pipeline as `exchangeIdpToken`. Error handling mirrors
 * `exchangeIdpToken` byte-for-byte — the two functions share the response
 * envelope contract.
 */
export async function exchangeGoogleCode(
  input: ExchangeGoogleCodeInput,
): Promise<AuthExchangeResult> {
  const url = `${getApiBaseUrl()}/api/auth/exchange`

  let response: Response
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: {
        Accept: V1,
        'Content-Type': V1,
      },
      body: JSON.stringify({
        idp_kind: 'google',
        code: input.code,
        redirect_uri: input.redirect_uri,
      }),
    })
  } catch {
    throw new AuthTransientError(0, 'Network error during /api/auth/exchange')
  }

  if (response.ok) {
    const body = (await response.json()) as SessionTokenResponse
    const result: AuthExchangeResult = {
      user_uuid: body.user_uuid,
      session_jwt: body.session_jwt,
      exp_ms: body.expires_at,
      idp_kind: 'google',
    }
    saveAuth(result)
    return result
  }

  if (response.status === 401) {
    clearAuth()
    throw new AuthInvalidError()
  }

  if (response.status === 423) {
    const body = (await response.json().catch(() => ({}))) as LockedResponse
    const pending = body.error?.details?.pending_deletion_until ?? 0
    throw new AuthLockedError(pending)
  }

  if (response.status >= 500) {
    throw new AuthTransientError(response.status)
  }

  throw new AuthTransientError(
    response.status,
    `Unexpected status ${response.status}`,
  )
}
